import type { AppLocale } from '@ielts-buddy/shared'
import type React from 'react'
import type { ComponentType } from 'react'
import { useState } from 'react'
import { t } from '../i18n/messages.js'
import { useAuthStore } from '../shared/auth-store.js'
import { usePreferencesStore } from '../shared/preferences-store.js'
import {
  type SpeakingMode,
  type SpeakingPart,
  useRouter,
} from '../shared/router-store.js'
import { haptics } from '../shared/telegram.js'
import { ArrowRightIcon, MicIcon } from '../shared/ui/icons.js'
import { Avatar, Card } from '../shared/ui/index.js'

/**
 * Home screen — the first thing a user sees inside the mini app.
 *
 * Action-first dashboard. Two zones only:
 *  1. Greeting strip (avatar + name) for personalisation
 *  2. A module list with two visual tiers:
 *     - Speaking renders as a gradient hero card — it's the live, primary
 *       action. Premium shadow, gradient surface, arrow affordance.
 *     - Writing / Listening / Reading use the SAME hero composition but in an
 *       inverse style: white surface, green accents, lock affordance. This
 *       keeps the premium feeling while preserving hierarchy.
 *
 * Why split the tiers this way:
 *  - The first pass demotion (small compact cards in a grid) improved focus
 *    but felt too cheap next to the Speaking hero.
 *  - Inverse hero keeps card shape/spacing/shadow premium while colour still
 *    communicates "coming soon" vs "available now".
 *  - When a module ships we only switch status to `available`; it naturally
 *    upgrades from inverse hero to gradient hero without layout changes.
 *
 * Module heroes omit body blurbs (titles + badges only) to save vertical space
 * above BottomNav; Speaking’s fuller pitch lives in `home.speaking.ariaHero`
 * for assistive tech. Long `home.module.*.desc` / `home.speaking.blurb` strings
 * remain in i18n for the bot or future surfaces.
 *
 * Root uses `min-h-full` (not `tab-screen` / fixed `h-full`) so when quick
 * practice or future expandable rows grow taller than the viewport, the App
 * shell’s `overflow-y-auto` region scrolls naturally.
 *
 * The trust footer sits in a column after a `flex-1` main block so on short
 * screens it stays pinned to the bottom of the viewport (above BottomNav
 * padding); when content is tall it follows the scroll as the last block.
 *
 * Why no "what is this app?" hero/intro card:
 *  - Returning users (the bulk of sessions) don't need it on every visit.
 *  - Onboarding/value-prop copy belongs in the Telegram bot's /start message
 *    so it runs once at install, not every launch.
 */
function greetingKey(): string {
  const h = new Date().getHours()
  if (h < 5) return 'home.greet.midnight'
  if (h < 12) return 'home.greet.morning'
  if (h < 18) return 'home.greet.afternoon'
  return 'home.greet.evening'
}

export function HomeScreen() {
  const user = useAuthStore((s) => s.user)
  const locale = usePreferencesStore((s) => s.locale)
  const navigateToSpeaking = useRouter((s) => s.navigateToSpeaking)
  const [quickPracticeOpen, setQuickPracticeOpen] = useState(false)

  const firstName = user?.firstName ?? 'there'
  const initial = (firstName.trim()[0] ?? '?').toUpperCase()

  const handleSpeakingMode = (
    mode: SpeakingMode,
    part?: SpeakingPart,
  ): void => {
    haptics.medium()
    navigateToSpeaking(mode, part)
  }

  return (
    <div className="flex min-h-full flex-col gap-3 screen-padding animate-fade-in">
      {/* Greeting — kept compact so four module cards + trust line fit above BottomNav on common phone heights. */}
      <header className="shrink-0 pt-1">
        <div className="flex min-h-[2.5rem] items-center gap-3">
          <Avatar
            photoUrl={user?.photoUrl ?? null}
            initial={initial}
            size="sm"
          />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-text-muted">
              {t(locale, greetingKey())}
            </p>
            <h1 className="truncate text-xl font-semibold leading-tight tracking-tight text-brand-text">
              {firstName}
            </h1>
          </div>
        </div>
        {/* Accent rule — a thin gradient line that anchors the greeting
            area and visually separates it from the module cards below.
            Fades left-to-right from brand-border to transparent so it
            reads as a horizon rather than a divider. */}
        <div
          aria-hidden
          className="mt-2 h-px"
          style={{
            background:
              'linear-gradient(90deg, var(--brand-border-strong) 0%, var(--brand-border) 40%, transparent 100%)',
          }}
        />
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-2">
        {/* Speaking — one unified card: gradient hero on top, expandable
            quick-practice footer on the bottom. Keeping everything inside a
            single container makes it obvious all options belong to Speaking. */}
        <section className="shrink-0">
          <SpeakingCard
            open={quickPracticeOpen}
            onToggle={() => {
              haptics.select()
              setQuickPracticeOpen((v) => !v)
            }}
            onMode={handleSpeakingMode}
          />
        </section>

        {/* Upcoming modules — inverse-hero treatment. Same composition and card
            scale as Speaking, but white surface + green accents so they read as
            premium second-tier entries instead of placeholders. */}
        <section className="flex shrink-0 flex-col gap-2">
          <ModuleCard
            Icon={PencilGlyph}
            title={t(locale, 'home.module.writing.title')}
            status="soon"
            locale={locale}
          />
          <ModuleCard
            Icon={HeadphonesGlyph}
            title={t(locale, 'home.module.listening.title')}
            status="soon"
            locale={locale}
          />
          <ModuleCard
            Icon={BookGlyph}
            title={t(locale, 'home.module.reading.title')}
            status="soon"
            locale={locale}
          />
        </section>
      </div>

      {/* Trust line — flex-1 main above pulls this to the viewport bottom when content is short */}
      <footer className="shrink-0 pt-2 text-center text-[10px] leading-snug text-brand-text-subtle">
        {t(locale, 'home.footer')}
      </footer>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

type ModuleStatus = 'available' | 'soon'

interface ModuleCardProps {
  Icon: ComponentType<{ className?: string }>
  title: string
  status: ModuleStatus
  locale: AppLocale
  /** Right-side meta text shown next to the status pill (e.g. "~12 min"). */
  meta?: string
  onClick?: () => void
}

/**
 * Renders a module either as the gradient hero (available) or as a quieter
 * surface card (coming soon). Two branches because the two tiers are different
 * enough — different layout density, different colour weight, different
 * affordance — that a single template with conditionals would obscure both.
 */
function ModuleCard(props: ModuleCardProps) {
  const locale = usePreferencesStore((s) => s.locale)
  return props.status === 'available' ? (
    <AvailableModuleCard {...props} locale={locale} />
  ) : (
    <SoonModuleCard {...props} locale={locale} />
  )
}

/**
 * Gradient hero card for live modules. Same recipe as the cue card on
 * Speaking and the score card on Results — that centralisation is what
 * makes the three "this is the moment" surfaces feel like one product.
 */
function AvailableModuleCard({
  Icon,
  title,
  meta,
  onClick,
  locale,
}: ModuleCardProps) {
  return (
    <Card
      as="div"
      size="hero"
      tone="gradient"
      className="group cursor-pointer p-3.5 transition-transform active:scale-[0.99]"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') onClick?.()
      }}
    >
      <div className="w-full text-left">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/20">
            <Icon className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex items-center gap-2">
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider">
                {t(locale, 'home.badge.available')}
              </span>
              {meta && (
                <span className="text-[10px] text-white/75">{meta}</span>
              )}
            </div>
            <h2 className="text-base font-semibold leading-tight tracking-tight">
              {title}
            </h2>
          </div>
          <ArrowRightIcon className="h-5 w-5 shrink-0 opacity-90 transition-transform group-active:translate-x-1" />
        </div>
      </div>
    </Card>
  )
}

/**
 * Inverse hero for upcoming modules:
 *  - same size/layout rhythm as the live hero card
 *  - white surface + green accents (instead of white-on-gradient)
 *  - lock glyph + "Soon" pill make non-availability explicit
 */
function SoonModuleCard({ Icon, title, locale }: ModuleCardProps) {
  return (
    <Card
      as="div"
      size="hero"
      tone="surface"
      className="bg-brand-gradient-inverse-hero p-3.5"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-primary-soft text-brand-primary">
          <Icon className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex items-center gap-2">
            <span className="rounded-full border border-brand-border bg-brand-primary-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-primary">
              {t(locale, 'home.badge.soon')}
            </span>
            <span className="text-[10px] text-brand-text-subtle">
              {t(locale, 'home.badge.inDev')}
            </span>
          </div>
          <h3 className="text-base font-semibold leading-tight tracking-tight text-brand-text">
            {title}
          </h3>
        </div>
        <LockGlyph className="h-5 w-5 shrink-0 text-brand-text-subtle" />
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Module-list-only icon glyphs.
//
// Speaking uses the shared `MicIcon` (filled) so we keep a consistent mic
// glyph across the app (home + speaking screen). The "soon" modules use
// these stroked glyphs because they're decorative-only — they'll be swapped
// for filled variants when each module ships and becomes interactive.
// ---------------------------------------------------------------------------

function PencilGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4z" />
      <path d="m15 5 3 3" />
    </svg>
  )
}

function HeadphonesGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z" />
      <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
  )
}

function BookGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  )
}

interface SpeakingCardProps {
  open: boolean
  onToggle: () => void
  onMode: (mode: SpeakingMode, part?: SpeakingPart) => void
}

/**
 * Single unified card for the Speaking module.
 *
 * Layout: gradient hero section (full mock, primary CTA) on top, plus a
 * collapsible white footer for quick-practice options. Keeping both inside
 * one card makes it visually obvious they're all speaking practice — the
 * earlier approach of a floating toggle below the hero card broke that link.
 */
function SpeakingCard({ open, onToggle, onMode }: SpeakingCardProps) {
  const locale = usePreferencesStore((s) => s.locale)
  return (
    <div
      className="overflow-hidden rounded-2xl"
      style={{ boxShadow: 'var(--brand-shadow-hero)' }}
    >
      {/* ── Gradient hero: full mock exam ── */}
      <div
        className="cursor-pointer bg-brand-gradient-hero p-3.5 text-white transition-transform active:scale-[0.99]"
        role="button"
        tabIndex={0}
        aria-label={t(locale, 'home.speaking.ariaHero')}
        onClick={() => onMode('full_test')}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') onMode('full_test')
        }}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/20">
            <MicIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex items-center gap-2">
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider">
                {t(locale, 'home.speaking.badge')}
              </span>
              <span className="text-[10px] text-white/75">
                {t(locale, 'home.speaking.duration')}
              </span>
            </div>
            <h2 className="text-base font-semibold leading-tight tracking-tight">
              {t(locale, 'home.speaking.title')}
            </h2>
          </div>
          <ArrowRightIcon className="h-5 w-5 shrink-0 opacity-90" />
        </div>
      </div>

      {/* ── White footer: quick practice toggle ── */}
      <div className="bg-brand-surface border-t border-brand-border">
        <button
          type="button"
          className="group flex min-h-[40px] w-full items-center justify-between px-3.5 py-1.5"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls="quick-practice-panel"
          aria-label={t(locale, 'home.speaking.ariaQuickToggle')}
        >
          <span className="flex items-center gap-2">
            <BoltGlyph className="w-3.5 h-3.5 text-brand-primary" />
            <span className="text-[11px] font-semibold tracking-wide text-brand-text-muted">
              {t(locale, 'home.quickPractice')}
            </span>
          </span>
          <ChevronIcon
            className="w-3.5 h-3.5 text-brand-text-subtle"
            style={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 200ms ease',
            }}
          />
        </button>

        {/* Expandable 2×2 grid.
            Uses grid-template-rows: 0fr → 1fr instead of max-height so the
            transition covers the exact natural height with no magic numbers.
            The inner div needs overflow:hidden + min-height:0 for this to work. */}
        <div
          id="quick-practice-panel"
          role="region"
          aria-label={t(locale, 'home.speaking.ariaQuickPanel')}
          style={{
            display: 'grid',
            gridTemplateRows: open ? '1fr' : '0fr',
            transition: 'grid-template-rows 220ms ease',
          }}
        >
          <div style={{ overflow: 'hidden', minHeight: 0 }}>
            <div className="grid grid-cols-2 gap-1.5 px-3 pb-3">
              <PracticeTile
                icon={<BoltGlyph className="w-4 h-4" />}
                title={t(locale, 'home.tile.warm.title')}
                ariaLabel={t(locale, 'home.tile.warm.aria')}
                meta={t(locale, 'home.tile.warm.meta')}
                onClick={() => onMode('warm_up')}
              />
              <PracticeTile
                icon={<span className="text-sm font-bold leading-none">1</span>}
                title={t(locale, 'home.tile.p1.title')}
                ariaLabel={t(locale, 'home.tile.p1.aria')}
                meta={t(locale, 'home.tile.p1.meta')}
                onClick={() => onMode('section', 1)}
              />
              <PracticeTile
                icon={<span className="text-sm font-bold leading-none">2</span>}
                title={t(locale, 'home.tile.p2.title')}
                ariaLabel={t(locale, 'home.tile.p2.aria')}
                meta={t(locale, 'home.tile.p2.meta')}
                onClick={() => onMode('section', 2)}
              />
              <PracticeTile
                icon={<span className="text-sm font-bold leading-none">3</span>}
                title={t(locale, 'home.tile.p3.title')}
                ariaLabel={t(locale, 'home.tile.p3.aria')}
                meta={t(locale, 'home.tile.p3.meta')}
                onClick={() => onMode('section', 3)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface PracticeTileProps {
  icon: React.ReactNode
  title: string
  ariaLabel: string
  meta: string
  onClick: () => void
}

/**
 * Compact tappable tile inside the SpeakingCard footer. No shadow — it sits
 * inside a card already. Uses a border + tinted background instead.
 */
function PracticeTile({
  icon,
  title,
  ariaLabel,
  meta,
  onClick,
}: PracticeTileProps) {
  return (
    <div
      className="flex min-h-[44px] cursor-pointer flex-col justify-center gap-1.5 rounded-xl border border-brand-border bg-brand-bg-soft p-2 transition-transform active:scale-[0.97]"
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') onClick()
      }}
    >
      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-brand-primary-soft text-brand-primary-strong">
        {icon}
      </div>
      <div>
        <p className="text-[12px] font-semibold leading-tight text-brand-text">
          {title}
        </p>
        <p className="mt-0.5 text-[10px] leading-tight text-brand-text-muted">
          {meta}
        </p>
      </div>
    </div>
  )
}

function BoltGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden
    >
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  )
}

function ChevronIcon({
  className,
  style,
}: {
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      style={style}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

function LockGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  )
}
